import { LoaderComponent } from '@/pages/loader/loader.component';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { HttpClientModule } from '@angular/common/http';
import { UserService } from '@/services/user.service';
import { LoginService } from '@/services/login.service';
import { ToastModule } from 'primeng/toast';
import { IconFieldModule } from 'primeng/iconfield';
import { ConfirmDialog } from "primeng/confirmdialog";
import { ButtonModule } from 'primeng/button';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterModule, HttpClientModule, LoaderComponent, ProgressSpinnerModule, ToastModule, IconFieldModule, ConfirmDialog, ButtonModule],
    /* The confirm dialog is rendered in an overlay outside this component's DOM,
       so its styles below are declared globally rather than scoped. They are all
       namespaced under .app-confirm* to keep that safe. */
    encapsulation: ViewEncapsulation.None,
    template: `<p-toast styleClass="z-9999"/>

    <!-- One confirmation design for the whole app. The headless template replaces
         ConfirmDialog's own header/content/footer, so every confirmationService.confirm()
         call renders as this title + message + Yes/No pair regardless of the icon,
         severity or button labels it passes. -->
    <p-confirmdialog styleClass="app-confirm-dialog">
        <ng-template #headless let-confirmation let-onAccept="onAccept" let-onReject="onReject">
            <div class="app-confirm">
                <h2 class="app-confirm-title">{{ confirmation.header }}</h2>

                <!-- Messages are authored with markup for emphasis, e.g. delete <b>"Levis"</b>. -->
                <div class="app-confirm-message" [innerHTML]="confirmation.message"></div>

                <div class="app-confirm-actions">
                    <p-button label="Yes" (onClick)="onAccept()" />
                    <p-button label="No" (onClick)="onReject()" />
                </div>
            </div>
        </ng-template>
    </p-confirmdialog>

    <app-loader></app-loader>

    <router-outlet></router-outlet>

    `,
    styles: [
        `
            /* .p-dialog is on the same element — qualified so this wins over the
               theme's own radius whichever order the stylesheets land in. */
            .p-dialog.app-confirm-dialog {
                width: 60vw;
                max-width: calc(100vw - 2rem);
                border-radius: 8px;
            }

            .app-confirm {
                padding: 1.75rem 2rem;
            }

            .app-confirm-title {
                margin: 0;
                font-size: 1.2rem;
                font-weight: 700;
                line-height: 1.3;
                color: var(--heading-color);
            }

            .app-confirm-message {
                margin-top: 1rem;
                font-size: 0.95rem;
                line-height: 1.6;
                color: var(--text-color);
            }

            .app-confirm-message b,
            .app-confirm-message strong {
                font-weight: 700;
                color: var(--heading-color);
            }

            /* Both actions are plain primary buttons — neither is styled as a
               destructive or secondary alternative. */
            .app-confirm-actions {
                display: flex;
                justify-content: flex-end;
                gap: 0.75rem;
                margin-top: 1.75rem;
            }

            // .app-confirm-actions .p-button {
            //     min-width: 5rem;
            //     justify-content: center;
            //     font-weight: 600;
            // }
        `
    ]
})
export class AppComponent implements OnInit{

  private refreshTokenTimeout: any;

  constructor(private userService: UserService, private router: Router, private loginService: LoginService){
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && this.loginService.isAuthenticated()) {
        const currentUrl = event.urlAfterRedirects;
        if (currentUrl.includes('bre-configuration')) {
         localStorage.removeItem('bre_conditional_output_tags');
        }
      }
    });
  }

  ngOnInit(): void {    
    this.userService.checkUSerInfoAndLoad();
    this.setupTokenRefresh();
  }

  setupTokenRefresh() {
    // 22 hours in milliseconds
    const interval =   22 * 60 * 60 * 1000;
    // Immediately schedule the first refresh
    this.scheduleTokenRefresh(interval);
  }

  scheduleTokenRefresh(interval: number) {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshAccessToken();
      // Schedule the next refresh
      this.scheduleTokenRefresh(interval);
    }, interval);
  }

  refreshAccessToken() {
    if (this.loginService.isAuthenticated()) {
      const refreshToken = this.loginService.getRefreshToken();
      if (refreshToken) {
        this.loginService.refreshToken(refreshToken).subscribe({
          next: (res: any) => {
            if (res && res.access) {
              this.loginService.setAccessToken(res.access);
            }
          },
          error: (err) => {
            console.error('Token refresh failed:', err);
            // Optionally, logout or notify user
          }
        });
      }
    }
  }

}
